import { type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ShieldAlert } from 'lucide-react';
import { CalendarDate, isoWeekKey } from '@apm/contracts';
import { SectionHeading } from '../../components/ui/section-heading';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, dashboardKeys } from '../../features/dashboard/api';
import { MyOkrSection } from '../../features/performance/components/my-okr-section';
import { MyPerformancePanel } from '../../features/performance/components/my-performance-panel';
import { TimesheetSection } from '../../features/timesheet/components/timesheet-section';
import { TeamPerformancePanel } from '../../features/performance/components/team-performance-panel';
import { presetRange } from '../../features/performance/team-projects-model';
import { todayIso } from '../../lib/overdue';
import { seesPeopleData } from '../../features/performance/visibility';
import { parseDate, today } from '../../lib/date';

/**
 * Cờ mở/đóng đi qua URL — `?team=0` cũng như `?team=false` là **đóng**.
 *
 * ⚠️ Router phân tích giá trị bằng `JSON.parse`, nên `?team=1` tới schema dưới
 * dạng **number** `1`, không phải chuỗi `'1'`. Một `z.literal('1')` sẽ trượt và
 * tham số biến mất im lặng khỏi URL — mất luôn khả năng gửi cho nhau đúng cảnh
 * (APM-903b).
 */
const OpenFlag = z
  .union([z.boolean(), z.literal(1), z.literal(0)])
  .optional()
  /**
   * Ra `false` **hoặc `undefined`** — mặc định là **mở**.
   *
   * Chỉ trạng thái **khác mặc định** mới lên URL. Mọi lời gọi `navigate` ở đây
   * đều trải `...search`, nên một `true` thật sẽ bám vào URL của mọi người dùng
   * sau cú bấm đầu tiên dù chẳng nói thêm điều gì.
   */
  .transform((value) => (value === false || value === 0 ? false : undefined));

export const PerformanceSearch = z.object({
  /** `false` = bảng nhóm đang **đóng**. Mặc định mở. */
  team: OpenFlag,
  /**
   * Thứ tự của bảng nằm ở URL để chia sẻ được, nhưng **không** ở query key của
   * React Query: sắp lại là việc của trình duyệt trên dữ liệu đã tải, còn mỗi
   * lượt gọi API là một lô dòng audit (xem `performanceApi.team`).
   */
  teamSort: z.string().optional(),
  teamDir: z.enum(['asc', 'desc']).optional(),
  /**
   * Tuần của bảng chấm công, dạng ISO week (`2026-W39`).
   *
   * ⚠️ `z.string()` ở đây là **đúng**, khác với `?team=1`: router phân tích giá
   * trị bằng `JSON.parse`, và `JSON.parse('2026-W39')` ném — nên giá trị tới
   * schema vẫn là chuỗi. Bẫy `APM-903b` chỉ áp cho thứ `JSON.parse` đọc được.
   */
  week: z.string().optional(),
  /**
   * Tab đang xem. Mặc định `me`.
   *
   * ⚠️ Tab nằm ở URL chứ không ở `useState`: "xem hiệu suất nhóm tuần trước" là
   * một thứ người ta gửi cho nhau, và một tab chỉ sống trong bộ nhớ component
   * thì đường dẫn gửi đi luôn mở ra tab đầu tiên.
   */
  perfTab: z.enum(['me', 'team']).optional(),
  /**
   * Kỳ của cả khối "Hiệu suất đội" — tổng quan và hai tab (APM-TPR-b, dùng chung
   * từ APM-TPR2-b; giữ tên cũ để đường dẫn đã gửi vẫn mở đúng kỳ) và cách nhìn
   * của "Nhân sự × dự án". Ngày `YYYY-MM-DD` là chuỗi `JSON.parse` không đọc
   * nổi, nên `z.string()` ở đây đúng (bẫy APM-903b).
   */
  projFrom: CalendarDate.optional().catch(undefined),
  projTo: CalendarDate.optional().catch(undefined),
  projBy: z.enum(['people', 'projects', 'matrix']).optional().catch(undefined),
  /**
   * Kỳ của tab "Hiệu suất của tôi" — APM-PERFME-c. **Tách** khỏi `projFrom`/
   * `projTo`: hai tab là hai chủ ngữ, và đổi kỳ ở tab này không được lặng lẽ
   * đổi kỳ của tab kia. Chuỗi ngày `JSON.parse` không đọc nổi ⇒ schema chuỗi
   * đúng (bẫy APM-903b).
   */
  meFrom: CalendarDate.optional().catch(undefined),
  meTo: CalendarDate.optional().catch(undefined),
});

export const Route = createFileRoute('/_app/performance')({
  validateSearch: PerformanceSearch,
  component: PerformancePage,
});

/**
 * Hiệu suất của tôi — APM-PERF2-a, bảng nhóm vào ở `-f`.
 *
 * ⚠️ **Không** gắn quyền ở route, đúng lý lẽ đã có ở `/velocity`: ai cũng xem
 * được hiệu suất của **chính mình** (spec mục 10 đòi minh bạch với người bị
 * đo). Gắn quyền ở đây sẽ ẩn trang khỏi đúng những người mà nó nói về, và biến
 * tính năng này thành một hộp đen chấm điểm người.
 *
 * ## Hai tab cùng một khuôn — APM-PERFME-c
 *
 * | Tab "của tôi" | Tab "đội" |
 * | --- | --- |
 * | [kỳ] Tổng quan · Theo dự án · Mức độ cập nhật | [kỳ] Tổng quan · Theo nhân sự · Nhân sự × dự án · Mức độ cập nhật |
 * | Timesheet (tuần riêng) · OKR | Timesheet (tuần riêng) |
 *
 * Mỗi tab một bộ chọn kỳ (`meFrom`/`meTo`, `projFrom`/`projTo`) cho mọi khối
 * của nó, trừ Timesheet. OKR vẫn đứng cuối tab đầu: nó là **cam kết**, đọc sau
 * các con số đo được (APM-OKR3-b).
 *
 * Phạm vi của khối cuối do API quyết (`VelocityService.subjects` đi theo chuỗi
 * quản lý, APM-PERF2-e) — không phải do route này.
 */
function PerformancePage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
  });

  const seesTeam = seesPeopleData(abilities.data);
  /*
    Không có cấp dưới thì không có tab thứ hai — và cũng không được **ở** tab
    thứ hai: một đường dẫn `?perfTab=team` gửi cho người không quản ai sẽ mở ra
    một tab chắc chắn 403.
  */
  const tab = seesTeam ? (search.perfTab ?? 'me') : 'me';
  const week = search.week ?? isoWeekKey(parseDate(today()));

  return (
    <div className="flex flex-col gap-6 max-sm:gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-medium">{t('performance.tabTitle')}</h1>
      </header>

      {/*
        Hai tab — người dùng yêu cầu 2026-09-22 (*"bố trí rất khém, khó nhìn và
        không rõ ràng trong việc phân chia cho từng vùng"*).

        Ranh giới giữa hai tab là **chủ ngữ**, không phải loại dữ liệu: tab đầu
        nói về "tôi", tab sau nói về "người khác". Đó cũng đúng là ranh giới
        quyền và ranh giới ghi sổ truy cập, nên một lời nhắc cho cả tab thay được
        hai lời nhắc nhỏ ở hai khối cạnh nhau.

        Bên trong mỗi tab: các khối theo kỳ trước, Timesheet sau, và ở tab đầu
        OKR cuối cùng — xem docblock của `PerformancePage`.
      */}
      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: { ...search, perfTab: next === 'me' ? undefined : (next as 'team') },
          })
        }
      >
        <TabsList variant="glass">
          <TabsTrigger value="me">{t('performance.tabs.me')}</TabsTrigger>
          {seesTeam && <TabsTrigger value="team">{t('performance.tabs.team')}</TabsTrigger>}
        </TabsList>
      </Tabs>

      {tab === 'me' ? (
        <div className="flex flex-col gap-4">
          {/*
            Làm lại theo khuôn "Hiệu suất đội" — APM-PERFME-c (người dùng yêu
            cầu 2026-10-14, chọn **chỉ** báo cáo mới: Sức chứa và Hồ sơ hiệu
            suất bỏ khỏi tab này). Một bộ chọn kỳ cho tổng quan, bảng theo dự án
            và mức độ cập nhật; Timesheet giữ tuần riêng, OKR giữ kỳ OKR.
          */}
          <Block>
            <MyPerformancePanel
              from={search.meFrom ?? presetRange('thisMonth', todayIso()).from}
              to={search.meTo ?? todayIso()}
              onRangeChange={(range) =>
                void navigate({ search: { ...search, meFrom: range.from, meTo: range.to } })
              }
            />
          </Block>

          <Block>
            <TimesheetSection
              week={week}
              scope="me"
              onWeekChange={(next) => void navigate({ search: { ...search, week: next } })}
            />
          </Block>

          <Block>
            <MyOkrSection />
          </Block>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/*
            Một lời nhắc cho **cả tab**: hai khối dưới đây đều ghi
            `sensitive_access_log` cho từng người khi tải, và người bị ghi đọc
            được điều đó ở trang "Quyền riêng tư". Nó đứng ở đây, trên cùng, vì
            một cảnh báo chỉ đọc được sau khi hành động đã xảy ra thì không cảnh
            báo gì cả — mà ở tab này thì hành động là chính cú bấm vào tab.
          */}
          <p className="inline-flex items-start gap-1.5 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/5 p-2 text-xs text-muted-foreground">
            <ShieldAlert aria-hidden className="mt-0.5 size-3.5 shrink-0 text-rag-amber" />
            {t('performance.teamAuditNotice')}
          </p>

          <Block>
            <TeamPerformancePanel
              from={search.projFrom ?? presetRange('thisMonth', todayIso()).from}
              to={search.projTo ?? todayIso()}
              grouping={search.projBy ?? 'people'}
              sort={{ sort: search.teamSort ?? 'userName', dir: search.teamDir ?? 'asc' }}
              onRangeChange={(range) =>
                void navigate({ search: { ...search, projFrom: range.from, projTo: range.to } })
              }
              onGroupingChange={(next) =>
                void navigate({
                  search: { ...search, projBy: next === 'people' ? undefined : next },
                })
              }
              onSortChange={(next) =>
                void navigate({
                  search: { ...search, teamSort: next.sort ?? undefined, teamDir: next.dir },
                })
              }
            />
          </Block>

          <Block>
            <TimesheetSection
              week={week}
              scope="team"
              onWeekChange={(next) => void navigate({ search: { ...search, week: next } })}
            />
          </Block>
        </div>
      )}
    </div>
  );
}

/**
 * Một mục trên trang — người dùng yêu cầu 2026-09-22 (*"có phân cách riêng cho
 * từng mục cho rõ ràng"*).
 *
 * Bản đầu chỉ có một **đường kẻ trên**, theo khuôn của tab Tổng quan. Người
 * dùng xem rồi nói vẫn chưa rõ — và đúng: một đường kẻ mảnh giữa hai khối cao
 * cả trăm pixel thì mắt không đọc ra ranh giới, nó chỉ đọc ra một trang dài.
 * Một khung kín có nền nói ra "đây là một mục, hết mục" mà không phải đọc chữ.
 *
 * Con của khối **không** tự vẽ khung nữa (`PerformanceCard`, bảng nhóm): khung
 * trong khung là hai ranh giới cho một thứ, và cái trong luôn trông như một
 * mục con không tồn tại.
 *
 * `title` bỏ trống cho những khối **tự có tiêu đề** (Timesheet, OKR, bảng
 * nhóm): thêm một tiêu đề nữa ở đây là hai dòng tiêu đề chồng nhau cho cùng
 * một thứ.
 */
function Block({
  icon,
  title,
  children,
}: {
  readonly icon?: LucideIcon;
  readonly title?: string;
  readonly children: ReactNode;
}) {
  return (
    // Điện thoại: lề 12px thay 16px — mỗi khối mất 8px bề ngang cho hai mép, và
    // bảng/lưới bên trong là thứ thiếu chỗ nhất trang (2026-10-07).
    <section className="flex min-w-0 flex-col gap-3 rounded-DEFAULT border border-border bg-background p-4 max-sm:p-3">
      {icon && title && <SectionHeading icon={icon}>{title}</SectionHeading>}
      {children}
    </section>
  );
}
